import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { XMarkIcon, PlusIcon, PhotoIcon, Bars3Icon, ChevronUpIcon, ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { Link } from 'react-router-dom';

const CreateLearningPath = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [loading, setLoading] = useState(false);
    const [availableItems, setAvailableItems] = useState({ posts: [], tasks: [] });
    const [selectedItems, setSelectedItems] = useState([]);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        difficulty_level: 'beginner',
        estimated_hours: '',
        image_url: '',
        color: '#3B82F6'
    });

    const [showItemSelector, setShowItemSelector] = useState(false);
    const [itemType, setItemType] = useState('post');
    const [activeTab, setActiveTab] = useState('posts');

    useEffect(() => {
        fetchAvailableItems();
        if (isEdit) {
            fetchPathData();
        }
    }, [id]);

    const fetchPathData = async () => {
        try {
            const response = await api.get(`/learning-paths/${id}`);
            if (response.data.success) {
                const path = response.data.data;
                setFormData({
                    title: path.title,
                    description: path.description,
                    difficulty_level: path.difficulty_level,
                    estimated_hours: path.estimated_hours,
                    image_url: path.image_url || '',
                    color: path.color || '#3B82F6'
                });
                
                const items = path.items.map(item => ({
                    id: `${item.item_type}-${item.item_id}`,
                    item_type: item.item_type,
                    item_id: item.item_id,
                    item_title: item.item_title,
                    item_description: item.item_description,
                    item_image: item.item_image,
                    difficulty: item.difficulty,
                    is_required: item.is_required
                }));
                setSelectedItems(items);
            }
        } catch (error) {
            console.error('Error fetching path data:', error);
        }
    };

    const fetchAvailableItems = async () => {
        try {
            const response = await api.get('/learning-paths/available-items');
            if (response.data.success) {
                setAvailableItems(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching available items:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await api.post('/learning-paths/upload-image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                setFormData(prev => ({
                    ...prev,
                    image_url: response.data.data.imageUrl
                }));
            } else {
                alert('Image upload failed');
            }
        } catch (error) {
            console.error('Image upload error:', error);
            alert('Image upload failed');
        }
    };

    const addItem = (item, type) => {
        const newItem = {
            id: `${type}-${item.id}`,
            item_type: type,
            item_id: item.id,
            item_title: item.title,
            item_description: type === 'post' ? item.excerpt : item.description,
            item_image: type === 'post' ? item.featured_image : item.image_url,
            difficulty: type === 'task' ? item.difficulty_level : null,
            is_required: true
        };
        
        setSelectedItems(prev => [...prev, newItem]);
        setShowItemSelector(false);
    };

    const removeItem = (itemId) => {
        setSelectedItems(prev => prev.filter(item => item.id !== itemId));
    };

    const moveItem = (fromIndex, toIndex) => {
        const items = Array.from(selectedItems);
        const [reorderedItem] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, reorderedItem);
        setSelectedItems(items);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('Form submitted with data:', formData);
        console.log('Selected items:', selectedItems);

        try {
            setLoading(true);
            
            const payload = {
                ...formData,
                estimated_hours: parseInt(formData.estimated_hours),
                items: selectedItems.map(item => ({
                    item_type: item.item_type,
                    item_id: item.item_id,
                    is_required: item.is_required
                }))
            };

            console.log('Sending payload:', payload);
            
            const response = isEdit 
                ? await api.put(`/learning-paths/${id}`, payload)
                : await api.post('/learning-paths', payload);
            
            console.log('Response:', response);
            
            if (response.data.success) {
                navigate('/learning-paths');
            } else {
                alert(isEdit ? 'Update operation failed' : 'Creation operation failed');
            }
        } catch (error) {
            console.error('Error creating learning path:', error);
            alert('Creation operation failed');
        } finally {
            setLoading(false);
        }
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'beginner': return 'bg-green-100 text-green-800';
            case 'intermediate': return 'bg-yellow-100 text-yellow-800';
            case 'advanced': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6 min-h-screen text-white p-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-white">
                    {isEdit ? 'Edit Learning Path' : 'Create New Learning Path'}
                </h1>
                <p className="text-gray-400 mt-1">
                    {isEdit ? 'Edit the existing learning path' : 'Create a new learning path for users'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-gray-700 shadow rounded-lg p-6">
                    <h2 className="text-lg font-medium text-white mb-4">Basic Information</h2>
                    
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-300">
                                Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                className="mt-1 block w-full bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-gray-700 rounded-md shadow-sm text-white placeholder-gray-500 focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                                placeholder="e.g., Web Development Fundamentals"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-300">
                                Description *
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                rows={3}
                                className="mt-1 block w-full bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-gray-700 rounded-md shadow-sm text-white placeholder-gray-500 focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                                placeholder="Provide information about the content and goals of this learning path..."
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300">
                                Difficulty Level
                            </label>
                            <select
                                name="difficulty_level"
                                value={formData.difficulty_level}
                                onChange={handleInputChange}
                                className="mt-1 block w-full bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-gray-700 rounded-md shadow-sm text-white focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                            >
                                <option value="beginner">Beginner</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300">
                                Estimated Duration (hours)
                            </label>
                            <input
                                type="number"
                                name="estimated_hours"
                                value={formData.estimated_hours}
                                onChange={handleInputChange}
                                min="1"
                                className="mt-1 block w-full bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-gray-700 rounded-md shadow-sm text-white placeholder-gray-500 focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                                placeholder="40"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300">
                                Cover Image
                            </label>
                            <div className="mt-1 space-y-2">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-cyan-400/10 file:text-cyan-300 hover:file:bg-cyan-400/20"
                                />
                                {formData.image_url && (
                                    <div className="mt-2">
                                        <img 
                                            src={formData.image_url} 
                                            alt="Preview" 
                                            className="h-20 w-20 object-cover rounded-lg border border-gray-600"
                                        />
                                    </div>
                                )}
                                <input
                                    type="text"
                                    name="image_url"
                                    value={formData.image_url}
                                    onChange={handleInputChange}
                                    className="block w-full bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-gray-700 rounded-md shadow-sm text-white placeholder-gray-500 focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                                    placeholder="Or paste image URL here"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300">
                                Theme Color
                            </label>
                            <input
                                type="color"
                                name="color"
                                value={formData.color}
                                onChange={handleInputChange}
                                className="mt-1 block w-full h-10 bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-cyan-400 focus:border-cyan-400"
                            />
                        </div>
                    </div>
                </div>

                {/* Content Items */}
                <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-gray-700 shadow rounded-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-medium text-white">Content Steps</h2>
                        <button
                            type="button"
                            onClick={() => setShowItemSelector(true)}
                            className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-cyan-400/10 to-cyan-600/10 text-cyan-300 rounded-lg border border-cyan-400/30 hover:from-cyan-400/20 hover:to-cyan-600/20 transition-all duration-300"
                        >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Add Content
                        </button>
                    </div>

                    {selectedItems.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-400">No content added yet. Click "Add Content" to start building your path.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {selectedItems.map((item, index) => (
                                <div key={item.id} className="bg-gradient-to-br from-[#0A0A0A] to-black border border-gray-600 rounded-lg p-4 flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="flex flex-col space-y-1">
                                            <button
                                                type="button"
                                                onClick={() => moveItem(index, Math.max(0, index - 1))}
                                                disabled={index === 0}
                                                className="p-1 text-gray-400 hover:text-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <ChevronUpIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => moveItem(index, Math.min(selectedItems.length - 1, index + 1))}
                                                disabled={index === selectedItems.length - 1}
                                                className="p-1 text-gray-400 hover:text-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <ChevronDownIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                        
                                        <div className="flex-shrink-0">
                                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-cyan-400/10 text-cyan-300 text-sm font-medium">
                                                {index + 1}
                                            </span>
                                        </div>
                                        
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2">
                                                <h4 className="text-sm font-medium text-white">{item.item_title}</h4>
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                                    item.item_type === 'post' 
                                                        ? 'bg-purple-400/10 text-purple-300 border border-purple-400/30'
                                                        : 'bg-emerald-400/10 text-emerald-300 border border-emerald-400/30'
                                                }`}>
                                                    {item.item_type === 'post' ? 'Blog Post' : 'Task'}
                                                </span>
                                                {item.difficulty && (
                                                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(item.difficulty)}`}>
                                                        {item.difficulty}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-400 mt-1">{item.item_description}</p>
                                        </div>
                                    </div>
                                    
                                    <button
                                        type="button"
                                        onClick={() => removeItem(item.id)}
                                        className="p-2 text-gray-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3">
                    <Link
                        to="/learning-paths"
                        className="px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg hover:bg-gray-800/50 transition-colors"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-gradient-to-r from-cyan-400/10 to-cyan-600/10 text-cyan-300 rounded-lg border border-cyan-400/30 hover:from-cyan-400/20 hover:to-cyan-600/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Processing...' : (isEdit ? 'Update Path' : 'Create Path')}
                    </button>
                </div>
            </form>

            {/* Item Selector Modal */}
            {showItemSelector && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] rounded-lg border border-gray-700 p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-white">Select Content</h3>
                            <button
                                onClick={() => setShowItemSelector(false)}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="border-b border-gray-700 mb-4">
                            <nav className="flex space-x-8">
                                <button
                                    onClick={() => setActiveTab('posts')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                        activeTab === 'posts'
                                            ? 'border-cyan-400 text-cyan-300'
                                            : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                                    }`}
                                >
                                    Blog Posts ({availableItems.posts?.length || 0})
                                </button>
                                <button
                                    onClick={() => setActiveTab('tasks')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                        activeTab === 'tasks'
                                            ? 'border-cyan-400 text-cyan-300'
                                            : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                                    }`}
                                >
                                    Tasks ({availableItems.tasks?.length || 0})
                                </button>
                            </nav>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {activeTab === 'posts' ? (
                                availableItems.posts?.length > 0 ? (
                                    availableItems.posts.map((post) => (
                                        <div key={post.id} className="bg-gradient-to-br from-[#0A0A0A] to-black border border-gray-600 rounded-lg p-4">
                                            <div className="flex items-start space-x-3">
                                                {post.featured_image && (
                                                    <img 
                                                        src={post.featured_image} 
                                                        alt={post.title}
                                                        className="w-16 h-16 rounded-lg object-cover"
                                                    />
                                                )}
                                                <div className="flex-1">
                                                    <h4 className="text-sm font-medium text-white mb-1">{post.title}</h4>
                                                    <p className="text-xs text-gray-400 mb-3 line-clamp-2">{post.excerpt}</p>
                                                    <button
                                                        onClick={() => addItem(post, 'post')}
                                                        className="text-xs px-3 py-1 bg-gradient-to-r from-purple-400/10 to-purple-600/10 text-purple-300 rounded border border-purple-400/30 hover:from-purple-400/20 hover:to-purple-600/20 transition-all"
                                                    >
                                                        Add Post
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-2 text-center py-8">
                                        <p className="text-gray-400">No blog posts available</p>
                                    </div>
                                )
                            ) : (
                                availableItems.tasks?.length > 0 ? (
                                    availableItems.tasks.map((task) => (
                                        <div key={task.id} className="bg-gradient-to-br from-[#0A0A0A] to-black border border-gray-600 rounded-lg p-4">
                                            <div className="flex items-start space-x-3">
                                                {task.image_url && (
                                                    <img 
                                                        src={task.image_url?.startsWith('http') ? task.image_url : `${process.env.REACT_APP_API_URL}${task.image_url}`} 
                                                        alt={task.title}
                                                        className="w-16 h-16 rounded-lg object-cover"
                                                    />
                                                )}
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        <h4 className="text-sm font-medium text-white">{task.title}</h4>
                                                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(task.difficulty_level)}`}>
                                                            {task.difficulty_level}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-400 mb-3 line-clamp-2">{task.description}</p>
                                                    <button
                                                        onClick={() => addItem(task, 'task')}
                                                        className="text-xs px-3 py-1 bg-gradient-to-r from-emerald-400/10 to-emerald-600/10 text-emerald-300 rounded border border-emerald-400/30 hover:from-emerald-400/20 hover:to-emerald-600/20 transition-all"
                                                    >
                                                        Add Task
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-2 text-center py-8">
                                        <p className="text-gray-400">No tasks available</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateLearningPath; 